import { isValid, parse, parseISO } from 'date-fns';

/**
 * Parse various date input formats to Date object using date-fns
 */
const parseDate = (input: Date | string): Date | null => {
  if (input instanceof Date) {
    return isValid(input) ? input : null;
  }

  if (typeof input === 'string') {
    // Try ISO string first (most common)
    if (/^\d{4}-\d{2}-\d{2}/.test(input)) {
      const isoDate = parseISO(input);
      if (isValid(isoDate)) return isoDate;
    }

    // Try common date formats
    const formats = [
      'yyyy.MM.dd', // 2025.04.09
      'yyyy. MM. dd', // 2025. 04. 09
      'MM/dd/yyyy', // 04/09/2025
      'yyyy/MM/dd', // 2025/04/09
      'dd-MM-yyyy', // 09-04-2025
      'MM-dd-yyyy', // 04-09-2025
    ];

    for (const formatStr of formats) {
      try {
        const parsedDate = parse(input, formatStr, new Date());
        if (isValid(parsedDate)) return parsedDate;
      } catch {
        continue;
      }
    }

    // Fallback to native Date parsing
    const nativeDate = new Date(input);
    if (isValid(nativeDate)) return nativeDate;
  }

  return null;
};

/**
 * 날짜를 로케일에 맞게 포맷팅합니다.
 * 한국어의 경우 마지막 온점을 제거하여 사용자 친화적으로 표시합니다.
 *
 * @param input - 포맷팅할 날짜 (Date 객체, '2025-04-09', '2025.04.09' 등)
 * @param _locale - 로케일 코드 (기본값: 'ko')
 * @returns 포맷팅된 날짜 문자열 (예: '2025. 4. 9')
 */
export const formatLocalizedDate = (
  input: Date | string,
  _locale = 'ko',
): string => {
  const date = parseDate(input);

  if (!date) {
    return '';
  }

  const formatted = Intl.DateTimeFormat(_locale).format(date);

  // 한국어의 경우 마지막 온점 제거
  // 공식 표기법은 "2025. 4. 9."이지만, 웹/앱에서는 사용자 친화적으로 "2025. 4. 9" 사용
  if (_locale === 'ko' && formatted.endsWith('.')) {
    return formatted.slice(0, -1);
  }

  return formatted;
};
